import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { InfoTip } from './InfoTip';
import { errorText } from '../lib/errorText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSE_LABELS = {
  chat: 'Saludando', happy: 'Celebrando', focused: 'Enfocado',
  thinking: 'Pensando', idea: 'Con una idea', search: 'Buscando', alert: 'En alerta',
};
const STYLE_LABELS = { butter: 'Amarillo marca', green: 'Verde', mint: 'Menta', copper: 'Cobre' };

export const StickerStudio = () => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ poses: [], styles: [] });
  const [form, setForm] = useState({ text: '', pose: 'chat', style: 'butter' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/noah/stickers/custom`);
      setItems(data.stickers || []);
      setMeta({ poses: data.poses || [], styles: data.styles || [] });
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (form.text.trim().length < 2) { toast.error('Escribe la frase del sticker'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/noah/stickers/custom`, form);
      toast.success('Sticker de marca creado');
      setForm({ ...form, text: '' });
      await load();
    } catch (e) {
      toast.error(errorText(e, 'No se pudo crear el sticker'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/noah/stickers/custom/${id}`);
      toast.info('Sticker eliminado');
      await load();
    } catch (e) {
      toast.error(errorText(e, 'No se pudo eliminar'));
    }
  };

  return (
    <Card className="bg-card/80 backdrop-blur border-white/10" data-testid="sticker-studio">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand-copper" />
          <h3 className="font-semibold text-foreground">Stickers de tu marca</h3>
          <InfoTip
            title="Stickers propios"
            text="Escribe tus frases de venta favoritas y Noah las convierte en stickers con su propia imagen. Podrá usarlos en los chats igual que los oficiales."
            source="Se generan en el servidor con la pose 3D de Noah y la paleta de la marca"
            to="/campaigns"
            linkLabel="Elegir cuáles usa cada campaña"
          />
          <span className="ml-auto text-[11px] text-muted-foreground">{items.length}/30</span>
        </div>

        <div className="grid sm:grid-cols-4 gap-2 items-end">
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs">Frase</Label>
            <Input data-testid="sticker-text" maxLength={60} placeholder="¡Hoy cierras tu venta!"
              value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Pose de Noah</Label>
            <Select value={form.pose} onValueChange={(v) => setForm({ ...form, pose: v })}>
              <SelectTrigger data-testid="sticker-pose"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta.poses.length ? meta.poses : Object.keys(POSE_LABELS)).map((p) => (
                  <SelectItem key={p} value={p}>{POSE_LABELS[p] || p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Color del globo</Label>
            <Select value={form.style} onValueChange={(v) => setForm({ ...form, style: v })}>
              <SelectTrigger data-testid="sticker-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta.styles.length ? meta.styles : Object.keys(STYLE_LABELS)).map((st) => (
                  <SelectItem key={st} value={st}>{STYLE_LABELS[st] || st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={create} disabled={busy} data-testid="sticker-create" className="gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear sticker
        </Button>

        {items.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 pt-1">
            {items.map((s) => (
              <div key={s.id} className="relative group rounded-2xl bg-muted/30 p-2">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${s.image}`} alt={s.text}
                  data-testid={`custom-sticker-${s.id}`} className="w-full h-auto" loading="lazy" />
                <p className="text-[10px] text-muted-foreground truncate mt-1">{s.text}</p>
                <button onClick={() => remove(s.id)} data-testid={`custom-sticker-delete-${s.id}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StickerStudio;
